import { useState } from 'react';

/**
 * The confirmation for anything destructive - RX-OPS-11.
 *
 * `confirm('Are you sure?')` is not a question anybody can answer. The rule
 * here: NAME what is about to be destroyed, say WHAT SURVIVES, and for the
 * worst of them make the person type the name.
 *
 * "Delete organisation" tells a reader nothing. "Delete Sunrise Care Home —
 * 34 residents, 12 staff accounts and 4,100 dose records. Staff keep their
 * own accounts. This cannot be undone." tells them everything, and is the
 * difference between a mistake and an informed decision.
 */
export interface Destructive {
  title: string;
  /** What is affected. Concrete counts, not "all associated data". */
  body: string;
  /** What survives. Often the more important half. */
  reassurance?: string;
  confirmLabel: string;
  /**
   * Require the exact text to be typed. For the irreversible ones only:
   * asking for it on every action teaches people to type without reading.
   */
  typeToConfirm?: string;
  /**
   * A change that is not a loss — linking a medicine to a time — is asked
   * the same careful way but confirmed with the ordinary green button. Red
   * is kept for what removes something.
   */
  tone?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
}

export function Confirm({
  title, body, reassurance, confirmLabel, typeToConfirm, tone = 'danger', onConfirm, onCancel,
}: Destructive & { onCancel: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  /*
   * The failure has to be shown HERE.
   *
   * A caller that catches its own error and renders a banner on the page
   * behind renders it underneath this overlay, where nobody will ever see it:
   * the dialog simply un-busies itself and the row does not disappear. So
   * this component owns the failure of the action it launched.
   */
  const [failed, setFailed] = useState<string | null>(null);
  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-md rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2 text-sm">{body}</p>
        {reassurance && (
          <p className="mt-2 rounded-lg border border-(--line) bg-(--panel) p-3 text-sm text-(--muted)">
            {reassurance}
          </p>
        )}

        {typeToConfirm && (
          <label className="mt-4 block">
            <span className="text-xs text-(--muted)">
              Type <strong className="text-(--fg)">{typeToConfirm}</strong> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3 text-sm"
            />
          </label>
        )}

        {failed && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500"
          >
            {failed} — nothing was changed.
          </p>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            onClick={onCancel}
            className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
          >
            Cancel
          </button>
          <button
            disabled={!ready || busy}
            onClick={async () => {
              setBusy(true);
              setFailed(null);
              try {
                await onConfirm();
                onCancel();
              } catch (error) {
                setFailed(error instanceof Error ? error.message : String(error));
              } finally {
                setBusy(false);
              }
            }}
            className={`min-h-11 rounded-lg px-4 text-sm font-medium text-white disabled:opacity-40 ${
              tone === 'primary' ? 'bg-(--accent)' : 'bg-red-600'
            }`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
